import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { OrganizationIntegrationListResponse } from 'src/models/organizationIntegration.models'
// getOrgIntegrationDetail/getOrgIntegrationsList are imported dynamically
// inside each thunk body below rather than statically here. This module is
// reachable from createRootReducer (manageOrgIntegrationPopup.reducer.ts and
// organizationIntegrations.reducers.ts import its action-type constants), and
// integrations.network.ts uses getRequestConfig(), which imports the
// clientStore singleton -> configureStore -> createRootReducer. A static
// import here closes that cycle and breaks store initialization; a dynamic
// import defers it past module load, once createRootReducer has finished
// evaluating.
import { RootState } from 'src/redux/createRootReducer'
import { BackendErrorPayload } from 'src/utils/error.utils'

export const GET_ORG_INTEGRATIONS_INIT = 'app/GET_ORG_INTEGRATIONS_INIT'
export const GET_ORG_INTEGRATIONS_SUCCESS = 'app/GET_ORG_INTEGRATIONS_SUCCESS'
export const GET_ORG_INTEGRATIONS_FAILURE = 'app/GET_ORG_INTEGRATIONS_FAILURE'

export type GetOrgIntegrationsSuccessPayload = {
  organizationConnections: OrganizationIntegrationListResponse[]
}

export type GetOrgIntegrationsFailurePayload = BackendErrorPayload<string>

export interface GetOrgIntegrationsInitAction {
  type: typeof GET_ORG_INTEGRATIONS_INIT
}
export interface GetOrgIntegrationsSuccessAction {
  type: typeof GET_ORG_INTEGRATIONS_SUCCESS
  payload: GetOrgIntegrationsSuccessPayload
}
export interface GetOrgIntegrationsFailureAction {
  type: typeof GET_ORG_INTEGRATIONS_FAILURE
  payload: GetOrgIntegrationsFailurePayload
}

export type GetOrgIntegrationsActions =
  | GetOrgIntegrationsInitAction
  | GetOrgIntegrationsSuccessAction
  | GetOrgIntegrationsFailureAction

export const createGetOrgIntegrationsInitAction =
  (): GetOrgIntegrationsInitAction => ({
    type: GET_ORG_INTEGRATIONS_INIT,
  })
export const createGetOrgIntegrationsSuccessAction = (
  payload: GetOrgIntegrationsSuccessPayload,
): GetOrgIntegrationsSuccessAction => ({
  type: GET_ORG_INTEGRATIONS_SUCCESS,
  payload,
})
export const createGetOrgIntegrationsFailureAction = (
  payload: GetOrgIntegrationsFailurePayload,
): GetOrgIntegrationsFailureAction => ({
  type: GET_ORG_INTEGRATIONS_FAILURE,
  payload,
})

export const getOrgIntegrations = (
  orgId: string,
): ThunkAction<Promise<void>, RootState, void, GetOrgIntegrationsActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetOrgIntegrationsActions>,
  ): Promise<void> => {
    dispatch(createGetOrgIntegrationsInitAction())
    try {
      const { getOrgIntegrationsList } = await import(
        'src/queries/organizations/integrations/integrations.network'
      )
      const response = await getOrgIntegrationsList(orgId)
      dispatch(createGetOrgIntegrationsSuccessAction(response))
    } catch (error) {
      dispatch(
        createGetOrgIntegrationsFailureAction(
          error as BackendErrorPayload<string>,
        ),
      )
    }
  }
}

export const GET_ORG_INTEGRATION_INIT = 'app/GET_ORG_INTEGRATION_INIT'
export const GET_ORG_INTEGRATION_SUCCESS = 'app/GET_ORG_INTEGRATION_SUCCESS'
export const GET_ORG_INTEGRATION_FAILURE = 'app/GET_ORG_INTEGRATION_FAILURE'

export type GetOrgIntegrationSuccessPayload = {
  organizationConnection: OrganizationIntegrationListResponse
}

export type GetOrgIntegrationFailurePayload = BackendErrorPayload<string>

export interface GetOrgIntegrationInitAction {
  type: typeof GET_ORG_INTEGRATION_INIT
}
export interface GetOrgIntegrationSuccessAction {
  type: typeof GET_ORG_INTEGRATION_SUCCESS
  payload: GetOrgIntegrationSuccessPayload
}
export interface GetOrgIntegrationFailureAction {
  type: typeof GET_ORG_INTEGRATION_FAILURE
  payload: GetOrgIntegrationFailurePayload
}

export type GetOrgIntegrationActions =
  | GetOrgIntegrationInitAction
  | GetOrgIntegrationSuccessAction
  | GetOrgIntegrationFailureAction

export const createGetOrgIntegrationInitAction =
  (): GetOrgIntegrationInitAction => ({
    type: GET_ORG_INTEGRATION_INIT,
  })
export const createGetOrgIntegrationSuccessAction = (
  payload: GetOrgIntegrationSuccessPayload,
): GetOrgIntegrationSuccessAction => ({
  type: GET_ORG_INTEGRATION_SUCCESS,
  payload,
})
export const createGetOrgIntegrationFailureAction = (
  payload: GetOrgIntegrationFailurePayload,
): GetOrgIntegrationFailureAction => ({
  type: GET_ORG_INTEGRATION_FAILURE,
  payload,
})

export const getOrgIntegration = (
  orgId: string,
  connectionId: string,
): ThunkAction<Promise<void>, RootState, void, GetOrgIntegrationActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetOrgIntegrationActions>,
  ): Promise<void> => {
    dispatch(createGetOrgIntegrationInitAction())
    try {
      const { getOrgIntegrationDetail } = await import(
        'src/queries/organizations/integrations/integrations.network'
      )
      const response = await getOrgIntegrationDetail(orgId, connectionId)
      dispatch(createGetOrgIntegrationSuccessAction(response))
    } catch (error) {
      dispatch(
        createGetOrgIntegrationFailureAction(
          error as BackendErrorPayload<string>,
        ),
      )
    }
  }
}
